import React, { useEffect, Suspense } from 'react'
import { Outlet, useParams, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { normalizeLanguage } from '../utils/langPath.ts'
import { trackMarketingEvent } from '../helpers/analytics.ts'
import { isValidLanguage } from '../types/i18n.ts'
import NotFound from '../pages/notFound.tsx'

const PRIVATE_ROUTE_PATTERN = /^\/(?:admin(?:\/|$)|unsubscribe(?:\/|$)|rsvp(?:\/|$)|weddingPlanner\/(?:login|register|[^/]+)(?:\/|$))/

const LangLayout: React.FC = () => {
	const { lang } = useParams<{ lang?: string }>()
	const { i18n } = useTranslation()
	const routeLanguage = normalizeLanguage(lang)
	const location = useLocation()
	const routeWithoutLanguage = location.pathname.replace(/^\/(?:hr|sr|bs|sl|de|en)(?=\/|$)/, '') || '/'
	const isPrivateRoute = PRIVATE_ROUTE_PATTERN.test(routeWithoutLanguage)

	useEffect(() => {
		// A public URL always has the same language, regardless of IP or preferences.
		if (i18n.language !== routeLanguage) i18n.changeLanguage(routeLanguage)
		localStorage.setItem('@language', routeLanguage)
		if (isPrivateRoute) document.documentElement.lang = routeLanguage
	}, [routeLanguage, i18n, isPrivateRoute])

	useEffect(() => {
		const robots = document.head.querySelector('meta[name="robots"]')
		const previousContent = robots?.getAttribute('content')

		if (isPrivateRoute && robots) {
			robots.setAttribute('content', 'noindex, nofollow')
		}

		if (!isPrivateRoute) {
			trackMarketingEvent('page_view', location.pathname)
		}

		return () => {
			if (robots && previousContent) robots.setAttribute('content', previousContent)
		}
	}, [isPrivateRoute, location.pathname])

	if (i18n.language !== routeLanguage) return <div className='loading-container'>Loading...</div>
	if (lang && !isValidLanguage(lang)) return <NotFound />

	return (
		<Suspense fallback={<div className="loading-container">Loading...</div>}>
			<Outlet />
		</Suspense>
	)
}

export default LangLayout
